import type React from 'react'
import { useIntl } from 'react-intl'

import { AnimatePresence } from 'motion/react'

import { type CarProps } from '@/components/atoms/Car'
import { Icons } from '@/components/atoms/Icons/Icons'
import { ChannelMessageCard, type ChannelMessageDeletionTrace } from '@/components/molecules/ChannelMessageCard'
import { type MessagingRailProps } from '@/components/organisms/MessagingRail'
import { MessagingShell } from '@/components/templates/MessagingShell'
import { TeamMessageForm } from '@/components/templates/TeamConversationView/TeamMessageForm'
import { TeamMessageFormProvider } from '@/components/templates/TeamConversationView/TeamMessageFormProvider'
import { useVoiceRecorder } from '@/hooks/useVoiceRecorder'
import { VoicePlaybackProvider } from '@/model/contexts/VoicePlaybackContext'

export type ChannelMessageDeletionItem = ChannelMessageDeletionTrace

export interface ChannelMessageItem {
  _id: string
  sender: 'volunteer' | 'admin'
  senderLabel: string
  createdAt: Date
  text?: string
  firebaseStoragePath?: string
  imageWidth?: number
  imageHeight?: number
  audioStoragePath?: string
  audioDurationMs?: number
  car?: Pick<CarProps, 'state' | 'carNumber' | 'carType'>
  deleted: boolean
  deletion?: ChannelMessageDeletionItem
}

export interface ChannelViewProps {
  rail: MessagingRailProps
  channelName: string
  muteLabel: string
  regionName: string
  piloteRegionId: number
  readOnly?: boolean
  readOnlyDate?: string
  eveningOpen: boolean
  hasUnread?: boolean
  messages: ChannelMessageItem[]
  muted?: boolean
  onToggleMute?: () => Promise<void> | void
  onSendMessage?: (message: string) => Promise<void> | void
  onSendVoiceMessage?: (blob: Blob, durationMs: number) => Promise<void>
  canDelete: boolean
  onDeleteMessage?: (messageId: string) => Promise<void> | void
}

export const ChannelView: React.FC<ChannelViewProps> = (props) => (
  <VoicePlaybackProvider>
    <ChannelViewContent {...props} />
  </VoicePlaybackProvider>
)

const ChannelViewContent: React.FC<ChannelViewProps> = ({
  rail,
  channelName,
  muteLabel,
  regionName,
  piloteRegionId,
  readOnly,
  readOnlyDate,
  eveningOpen,
  hasUnread,
  messages,
  muted,
  onToggleMute,
  onSendMessage,
  onSendVoiceMessage,
  canDelete,
  onDeleteMessage,
}) => {
  const intl = useIntl()
  const recorder = useVoiceRecorder()

  return (
    <MessagingShell
      rail={rail}
      regionName={regionName}
      piloteRegionId={piloteRegionId}
      readOnly={readOnly}
      readOnlyDate={readOnlyDate}>
      <aside className="rounded-corner flex w-[244px] flex-shrink-0 flex-col bg-white pb-6">
        <div className="rounded-t-corner flex h-12 items-center justify-center bg-teal-500 px-5 py-2 text-lg text-white select-none">
          {intl.formatMessage({ id: 'channel.publicHeader' })}
        </div>
        <div className="flex flex-1 flex-col">
          <div
            data-testid="channel-row"
            className="flex h-[62px] items-center justify-between border border-gray-200 bg-teal-100 px-2 py-4">
            <div className="flex items-center gap-2.5">
              <div className="flex items-center gap-1">
                <Icons.wavingHand className="size-6 text-teal-400" />
                <span className="text-lg text-gray-900 select-none">{channelName}</span>
              </div>
              {hasUnread && <span data-testid="channel-unread" className="size-3 rounded-full bg-teal-400" />}
            </div>
            {onToggleMute && (
              <button
                type="button"
                data-testid="channel-mute-toggle"
                aria-label={muteLabel}
                onClick={onToggleMute}
                className="flex size-[30px] cursor-pointer items-center justify-center rounded p-0.5 text-teal-500 hover:bg-gray-100">
                {muted ? (
                  <Icons.notificationsOff className="size-6" />
                ) : (
                  <Icons.notificationsActive className="size-6" />
                )}
              </button>
            )}
          </div>
        </div>
        <p className="px-6 text-sm text-gray-600 select-none">{intl.formatMessage({ id: 'channel.note' })}</p>
      </aside>

      <section className="rounded-corner flex flex-1 flex-col justify-stretch overflow-hidden bg-white">
        <div className="flex h-12 items-center justify-center px-3 py-2 select-none">
          <p className="text-sm font-bold text-gray-800">{intl.formatMessage({ id: 'channel.banner' })}</p>
        </div>
        <div className="h-3 w-full bg-gray-400" />
        <div className="flex flex-1 flex-col-reverse overflow-y-auto">
          {onSendMessage && (
            <TeamMessageFormProvider onSendMessage={onSendMessage}>
              <TeamMessageForm
                regionName={regionName}
                disabled={!eveningOpen}
                placeholder={intl.formatMessage({ id: 'channel.composer.placeholder' })}
                voice={onSendVoiceMessage && { recorder, onSend: onSendVoiceMessage }}
              />
            </TeamMessageFormProvider>
          )}
          {eveningOpen && messages.length === 0 && (
            <p className="p-6 text-center text-base text-gray-500 select-none">
              {intl.formatMessage({ id: 'channel.empty' })}
            </p>
          )}
          <AnimatePresence initial={false}>
            {messages.map(({ _id, ...message }) => (
              <ChannelMessageCard
                key={_id}
                {...message}
                canDelete={canDelete}
                onDelete={onDeleteMessage && (() => onDeleteMessage(_id))}
              />
            ))}
          </AnimatePresence>
        </div>
      </section>
    </MessagingShell>
  )
}
